import { History, RemoveRedEye } from '@mui/icons-material';
import {
  EditAppBar,
  useDialog,
  useConcurrentialAccess as usePrismaConcurrentialAccess,
} from '@prismamedia/one-components';
import type { FC } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { CategoryStatus } from '../../../../../__generated__/queries-web';
import { Skeletons } from '../../../../../components/Skeletons';
import { paths } from '../../../../../routing/Router/paths';
import { auth } from '../../../../../utils/auth';
import { CategoriesContext } from '../../../categories.context';
import { FormContext } from '../../../form.context';
import { Category, PageRouteParams } from '../../../types';
import { isPageEditView, navigateTo } from '../../../utils';
import { useCategoryConcurrentialAccess } from '../../../utils/categoryConcurrentialAccess';
import { PageHistory } from './components/PageHistory';
import { StatusMenu } from './components/StatusMenu';
import { Title } from './components/Title';
import {
  STATUS_MENU_PLACEHOLDER_HEIGHT,
  STATUS_MENU_PLACEHOLDER_WIDTH,
} from './constants';
import { useStyles } from './styles';

export const PageEditAppBar: FC = () => {
  const classes = useStyles();
  const { activeCategory, isCategoriesLoading, refetchCategories } = useContext(
    CategoriesContext,
  );
  const { form, handlers, isFormLoading } = useContext(FormContext);

  const { closeDialog, openDialog } = useDialog();
  const routeParams = useParams<PageRouteParams>();
  const history = useHistory();
  const {
    lockCategory,
    unlockCategory,
    retainCategoryLock,
  } = useCategoryConcurrentialAccess();

  const searchParams = useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  // State for preventing navigation back during new category redirect
  const [isRedirectingToNewCategory, setIsRedirectingToNewCategory] = useState(
    false,
  );

  const actions = useMemo(
    () => [
      {
        disabled: !activeCategory?.historyCount,
        id: 'history',
        label: 'Historique',
        icon: <History />,
        onClick: () =>
          openDialog(
            <PageHistory
              activeCategory={activeCategory}
              onCloseHistory={() => {
                closeDialog();
              }}
            />,
            { fullWidth: true },
          ),
      },
      {
        disabled: true,
        icon: <RemoveRedEye />,
        id: 'see-front',
        label: 'Visualiser',
        onClick: () => {},
      },
    ],
    [activeCategory, closeDialog, openDialog],
  );

  /**
   * We pushing back into the view list
   */
  const handleNavigationBack = useCallback(() => {
    // Don't navigate back if we're in the middle of redirecting to a new category
    if (isRedirectingToNewCategory) {
      return;
    }

    if (activeCategory) {
      // When lockedBy === null is the case an user go to an unlocked article and lock it
      if (
        auth.user?.id === activeCategory.lockedBy?.id ||
        activeCategory.lockedBy === null
      ) {
        unlockCategory(activeCategory.id).catch(() => {});
      }

      // Determine the target category:
      // When editing a category, always go back to the list view of that same category
      // This preserves the user's current context instead of jumping to parent

      // Check if we came from the "Modifier" button (from: 'Card') vs breadcrumbs (from: 'Breadcrumbs')
      const fromCard = searchParams.get('from') === 'Card';
      const fromBreadcrumbs = searchParams.get('from') === 'Breadcrumbs';

      // Get the category ID from route params (more reliable than context)
      const categoryIdFromRoute =
        routeParams.lvl5 ||
        routeParams.lvl4 ||
        routeParams.lvl3 ||
        routeParams.lvl2 ||
        (routeParams as any).lvl1;

      // Determine the target category for back navigation
      let targetCategoryId = categoryIdFromRoute;

      if (fromCard && activeCategory) {
        // When coming from "Modifier" button, go back to the parent of the edited category
        // This ensures we're at the right level in the hierarchy
        targetCategoryId = activeCategory.parent?.id || activeCategory.id;
      } else if (fromBreadcrumbs && activeCategory) {
        // When coming from breadcrumbs, check if this is a newly created category
        // If the category was just created (updatedAt === createdAt), go back to parent
        if (activeCategory.updatedAt === activeCategory.createdAt) {
          // This is a newly created category, go back to the parent's list view
          targetCategoryId = activeCategory.parent?.id;
        } else {
          // This is an existing category being edited, go back to its list view
          targetCategoryId = activeCategory.id;
        }
      }

      // Use the appropriate category ID for navigation
      const pathname = targetCategoryId
        ? generatePath(paths.PAGE_LIST, {
            brandKey: routeParams.brandKey,
            format: routeParams.format,
            lvl1: targetCategoryId,
          })
        : generatePath(paths.PAGE_LIST, {
            brandKey: routeParams.brandKey,
            format: routeParams.format,
          });

      navigateTo({
        pathname,
        opts: {
          method: 'REPLACE',
          search: searchParams.toString(),
        },
      });
    } else {
      // Fallback: navigate to the list view if no active category
      const pathname = generatePath(paths.PAGE_LIST, {
        brandKey: routeParams.brandKey,
        format: routeParams.format,
      });

      navigateTo({
        pathname,
        opts: {
          method: 'REPLACE',
          search: searchParams.toString(),
        },
      });
    }
  }, [
    activeCategory,
    routeParams,
    searchParams,
    unlockCategory,
    isRedirectingToNewCategory,
  ]);

  // Need this hack to disable concurrentialAccess while the context set the correct activeCategory
  // TODO refacto this shit -> CategoriesContext
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ready) {
      setReady(true);
    }
  }, [activeCategory, ready]);

  const onLock = useCallback(
    (firstLock) => {
      if (activeCategory && activeCategory.id) {
        if (firstLock) {
          return lockCategory(activeCategory.id);
        }
        return retainCategoryLock(activeCategory.id);
      }
      return Promise.resolve();
    },
    [activeCategory, lockCategory, retainCategoryLock],
  );

  usePrismaConcurrentialAccess({
    currentUser: auth.user,
    lockStatus: ready ? activeCategory : null,
    unlockTimeout: 600000,
    onLock,
    onNavigateBack: handleNavigationBack,
    disableLockForV0: true,
  });

  return (
    <EditAppBar
      actions={actions}
      additionalElement={
        isCategoriesLoading && isPageEditView() ? (
          <Skeletons
            height={STATUS_MENU_PLACEHOLDER_HEIGHT}
            width={STATUS_MENU_PLACEHOLDER_WIDTH}
          />
        ) : (
          <StatusMenu
            status={form.status}
            onStatusChangeAndSave={(newStatus: CategoryStatus) => {
              // Create updated form with new status
              const updatedForm = {
                ...form,
                status: newStatus,
              };

              // Save the form immediately
              handlers.handleFormSubmit(
                updatedForm,
                (newCategory?: Category) => {
                  // Form reinitialization after successful save
                  handlers.setForm(updatedForm, true);

                  // Only redirect if it's a new category creation
                  if (
                    newCategory &&
                    newCategory.updatedAt === newCategory.createdAt
                  ) {
                    refetchCategories();

                    // Check if we came from "Ajouter un élément"
                    const fromSpeedDial =
                      searchParams.get('from') === 'SpeedDial';

                    // Only update active category if NOT from speed dial and we have a parent
                    if (!fromSpeedDial && newCategory.parent?.id) {
                      // Keep current active category
                    }

                    // We redirect to the edit view of the new category
                    // Preserve the current search parameters and hash
                    const currentHash =
                      window.location.hash.split('#')[1] || '';
                    const editPathname = generatePath(paths.PAGE_EDIT, {
                      brandKey: routeParams.brandKey,
                      format: routeParams.format,
                      lvl1: newCategory.id,
                    });

                    navigateTo({
                      pathname: editPathname,
                      opts: {
                        method: 'REPLACE',
                        search: searchParams.toString(),
                        hash: currentHash,
                      },
                    });
                  }
                },
                { skipNotification: false },
              );
            }}
          />
        )
      }
      className={classes.appBar}
      {...(isPageEditView() && {
        concurrentialAccess: {
          lockStatus: { lockedBy: activeCategory?.lockedBy || auth.user },
          disableLockForV0: true,
        },
      })}
      navigationBackDisabled={false}
      title={<Title />}
      onNavigateBack={handleNavigationBack}
      save={{
        disabled: isCategoriesLoading,
        loading: isFormLoading,
        onClick: () => {
          handlers.handleFormSubmit(
            form,
            (newCategory?: Category) => {
              // Form reinitialization
              handlers.setForm(form, true);

              // Only redirect if it's a new category creation
              if (
                newCategory &&
                newCategory.updatedAt === newCategory.createdAt
              ) {
                // Set flag to prevent navigation back during redirect
                setIsRedirectingToNewCategory(true);

                refetchCategories();

                // Check if we came from "Ajouter un élément"
                const fromSpeedDial = searchParams.get('from') === 'SpeedDial';

                // Only update active category if NOT from speed dial and we have a parent
                if (!fromSpeedDial && newCategory.parent?.id) {
                  // Keep current active category
                }

                // We redirect to the edit view of the new category
                // Preserve the current search parameters and hash
                const currentHash = window.location.hash.split('#')[1] || '';
                const editPathname = generatePath(paths.PAGE_EDIT, {
                  brandKey: routeParams.brandKey,
                  format: routeParams.format,
                  lvl1: newCategory.id,
                });

                navigateTo({
                  pathname: editPathname,
                  opts: {
                    method: 'REPLACE',
                    search: searchParams.toString(),
                    hash: currentHash,
                  },
                });

                // Reset flag after a short delay to allow the redirect to complete
                setTimeout(() => {
                  setIsRedirectingToNewCategory(false);
                }, 1000);
              }
            },
            { skipNotification: false },
          );
        },
        requiredFields: null,
      }}
    />
  );
};
